import { useEffect, useState } from "react";
import { MailCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export const maskEmail = (e = "") => {
  const [u, d] = e.split("@");
  return u && d ? `${u[0]}***@${d}` : e;
};

export function useResendVerification() {
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const { t } = useLang();
  useEffect(() => {
    if (!cooldown) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.already_verified) toast.success(t("verify_already"));
      else { toast.success(t("verify_sent")); setCooldown(60); }
      return data;
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 429 && d?.retry_after) setCooldown(d.retry_after);
      toast.error(formatApiError(d) || t("verify_error"));
    } finally { setBusy(false); }
  };
  return { resend, cooldown, busy };
}

export default function VerifyEmailSheet() {
  const [open, setOpen] = useState(false);
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const { resend, cooldown, busy } = useResendVerification();
  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener("auk:email-not-verified", on);
    return () => window.removeEventListener("auk:email-not-verified", on);
  }, []);
  useEffect(() => { if (user?.email_verified) setOpen(false); }, [user?.email_verified]);
  if (!open || !user) return null;
  const check = async () => {
    await refresh();
    const { data } = await api.get("/auth/me").catch(() => ({ data: null }));
    if (data?.email_verified) { toast.success(t("verify_done")); setOpen(false); }
    else toast.error(t("verify_not_yet"));
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4 pb-20 sm:pb-4" onClick={() => setOpen(false)}>
      <div data-testid="verify-sheet" onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div className="w-11 h-11 rounded-2xl bg-[#fff8d6] flex items-center justify-center"><MailCheck size={22} className="text-[#c8102e]" /></div>
          <button onClick={() => setOpen(false)} data-testid="verify-sheet-close" className="p-1 rounded-full hover:bg-zinc-100"><X size={18} /></button>
        </div>
        <h2 className="font-display font-extrabold text-lg mt-3">{t("verify_title")}</h2>
        <p className="text-sm text-zinc-600 mt-1">{t("verify_body")} <span className="font-semibold" data-testid="verify-sheet-email">{maskEmail(user.email)}</span>.</p>
        <p className="text-[11px] text-zinc-400 mt-2">{t("verify_help")}</p>
        <div className="flex gap-2 mt-4">
          <button onClick={check} data-testid="verify-refresh-btn" className="flex-1 py-2.5 rounded-xl border border-zinc-200 text-sm font-semibold">{t("verify_check")}</button>
          <button onClick={resend} disabled={busy || cooldown > 0} data-testid="verify-resend-btn"
                  className="flex-1 py-2.5 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white text-sm font-semibold disabled:opacity-50">
            {cooldown > 0 ? `${t("verify_resend")} (${cooldown}s)` : t("verify_resend")}
          </button>
        </div>
      </div>
    </div>
  );
}
